'use client';

import type { AnimationItem } from 'lottie-web';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useLottieAnimation } from 'hooks/animation/useLottieAnimation';
import styles from './HealthTracker.module.css';

interface CardData {
  head: string;
  desc: string;
}

const HealthTrackerCard = ({ data }: { data?: CardData }) => {
  return (
    <div className={styles.healthTrackerCard}>
      {data && (
          <>
            <h3>{data.head}</h3>
            <p className="desc">{data.desc}</p>
          </>
        )}
      </div>
    );
};

interface HealthTrackerItems {
  colA?: string | React.ReactNode;
  colB?: string | React.ReactNode | CardData;
  colC?: string | React.ReactNode | CardData;
}
interface HealthTrackerProps {
  head?: string | React.ReactNode;
  image?: string;
  noMargin?: boolean;
  items?: HealthTrackerItems[];
}

export const HealthTracker = ({
  content,
  fullBg = false,
  id,
}: {
  content?: HealthTrackerProps;
  fullBg?: boolean;
  id?: string;
}) => {
    const pathname = usePathname();
    const animContainerRef = useRef<HTMLDivElement>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const lottieRef = useRef<AnimationItem | null>(null);

    const [lottieReady, setLottieReady] = useState(false);

    useLottieAnimation(animContainerRef, {
      path: '/lottie/ultrahumanX-rotate.json',
      renderer: 'svg',
      loop: true,
      autoplay: false,
      onInstance: (animation: AnimationItem) => {
        lottieRef.current = animation;
        setLottieReady(true);
      },
    });

    const [lottieInView, setLottieInView] = useState(false);

    useEffect(() => {
      const el = animContainerRef.current;
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => setLottieInView(!!entries[0]?.isIntersecting),
        { rootMargin: '200px' },
      );
      io.observe(el);
      return () => io.disconnect();
    }, []);

    useEffect(() => {
      const anim = lottieRef.current;
      if (!anim) return;
      if (lottieInView) {
        if (anim.isPaused) anim.play();
      } else if (!anim.isPaused) {
        anim.pause();
      }
    }, [lottieInView, lottieReady]);

    useEffect(() => {
      if (id && window.location.hash.includes(`#${id}`)) {
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
      }
    }, [id, pathname]);

    return (
      <section ref={sectionRef} className={`${styles.root} ${fullBg ? 'full-bg' : ''}`} id={id}>
        <div className="root-content">
          <h2 className="head">{content?.head}</h2>
          {content?.image ? (
            <div className="lottie-container" ref={animContainerRef}></div>
          ) : null}

          <div className="content">
            {content?.items &&
              content?.items?.map((item, index) => (
                <div className="row" key={index}>
                  <p className="col">{item?.colA}</p>
                  {typeof item?.colB === 'object' &&
                  item?.colB !== null &&
                  item?.colB &&
                  'head' in item.colB &&
                  'desc' in item.colB ? (
                    <div className="col card">
                      <HealthTrackerCard data={item?.colB as CardData} />
                    </div>
                  ) : (
                    <p className="col">{item?.colB as string}</p>
                  )}
                  {typeof item?.colC === 'object' &&
                  item?.colC !== null &&
                  item?.colC &&
                  'head' in item.colC &&
                  'desc' in item.colC ? (
                    <div className="col card">
                      <HealthTrackerCard data={item?.colC as CardData} />
                    </div>
                  ) : (
                    <p className="col">{item?.colC as string}</p>
                  )}
                </div>
              ))}
          </div>
        </div>
      </section>
    );
};
